import React from "react";
import api from "../../lib/api";
import { toast } from "sonner";
import { Bot, MessageSquare, Power } from "lucide-react";

const DEFAULT_PROMPT = `Ești consultantul AI al magazinului online de produse de securitate (camere, interfoane, smart home, automatizări porți, panouri solare, rețelistică). Răspunzi în limba română, clar și concis. Recomandă produse din catalog bazat pe nevoile clientului. Când recomanzi, menționează codul produsului. Dacă clientul întreabă despre instalare, menționează că oferim instalare profesională pentru toate pachetele.`;

export default function AdminChat() {
    const [cfg, setCfg] = React.useState(null);
    const [provider, setProvider] = React.useState({ ai_chat_provider: "emergent", ai_chat_openai_key: "", ai_chat_anthropic_key: "", ai_chat_openrouter_key: "" });
    const [saving, setSaving] = React.useState(false);
    const [testMsg, setTestMsg] = React.useState("");
    const [testReply, setTestReply] = React.useState("");
    const [testing, setTesting] = React.useState(false);

    React.useEffect(() => {
        api.get("/admin/chat/config").then(r => {
            setCfg(r.data);
            setProvider({
                ai_chat_provider: r.data.ai_chat_provider || "emergent",
                ai_chat_openai_key: r.data.ai_chat_openai_key || "",
                ai_chat_anthropic_key: r.data.ai_chat_anthropic_key || "",
                ai_chat_openrouter_key: r.data.ai_chat_openrouter_key || "",
            });
        });
    }, []);

    const save = async () => {
        setSaving(true);
        try {
            await api.put("/admin/chat/config", cfg);
            await api.put("/admin/chat/provider", provider);
            toast.success("Setări salvate");
        } catch { toast.error("Eroare la salvare"); }
        setSaving(false);
    };

    const testBot = async () => {
        if (!testMsg.trim()) return;
        setTesting(true); setTestReply("");
        try {
            // Save first so test uses current config
            await api.put("/admin/chat/config", cfg);
            await api.put("/admin/chat/provider", provider);
            const r = await api.post("/chat", { session_id: `admin_test_${Date.now()}`, message: testMsg });
            setTestReply(r.data.response);
        } catch (e) { setTestReply("Eroare: " + (e.response?.data?.detail || e.message)); }
        setTesting(false);
    };

    if (!cfg) return <div className="text-muted-foreground">Se încarcă...</div>;

    return (
        <div className="max-w-3xl" data-testid="admin-chat">
            <div className="overline mb-1">Administrare</div>
            <h1 className="font-heading text-3xl mb-2 flex items-center gap-3"><Bot className="h-7 w-7 text-primary" /> Asistent AI</h1>
            <p className="text-sm text-muted-foreground mb-6">Configurează bot-ul AI ce apare pe site. Dezactivează-l complet sau personalizează numele, salutul și comportamentul.</p>

            <div className="card-soft p-5 mb-4">
                <label className="flex items-center justify-between gap-3 cursor-pointer">
                    <div className="flex items-center gap-3">
                        <Power className={`h-5 w-5 ${cfg.ai_chat_enabled ? "text-primary" : "text-muted-foreground"}`} />
                        <div>
                            <div className="font-medium">Afișează asistentul AI pe site</div>
                            <div className="text-xs text-muted-foreground">Când e dezactivat, bula de chat nu apare și endpoint-ul /api/chat returnează 503.</div>
                        </div>
                    </div>
                    <div className="relative">
                        <input type="checkbox" checked={!!cfg.ai_chat_enabled} onChange={(e) => setCfg(c => ({ ...c, ai_chat_enabled: e.target.checked }))} className="sr-only peer" data-testid="chat-toggle" />
                        <div className="w-12 h-6 bg-muted rounded-full peer-checked:bg-primary transition-colors"></div>
                        <div className="absolute top-0.5 left-0.5 bg-background w-5 h-5 rounded-full transition-transform peer-checked:translate-x-6"></div>
                    </div>
                </label>
            </div>

            <div className="card-soft p-5 mb-4 space-y-3">
                <h3 className="font-heading text-lg">Provider & model</h3>
                <p className="text-xs text-muted-foreground">Alege sursa API pentru bot. Cheia universală este inclusă gratuit prin platformă. Poți conecta propria ta cheie OpenAI sau Anthropic dacă preferi.</p>
                <div className="flex flex-wrap gap-2" data-testid="provider-picker">
                    {[
                        ["emergent", "Cheie universală (inclusă — default)"],
                        ["openai", "OpenAI (cheia ta)"],
                        ["anthropic", "Anthropic (cheia ta)"],
                        ["openrouter", "OpenRouter (orice model)"],
                    ].map(([k, l]) => (
                        <label key={k} className={`flex items-center gap-2 px-3 py-2 rounded-xl border cursor-pointer ${provider.ai_chat_provider === k ? "border-primary bg-primary/5" : "border-border"}`}>
                            <input type="radio" name="provider" value={k} checked={provider.ai_chat_provider === k} onChange={() => setProvider(p => ({ ...p, ai_chat_provider: k }))} data-testid={`provider-${k}`} />
                            <span className="text-sm">{l}</span>
                        </label>
                    ))}
                </div>
                {provider.ai_chat_provider === "openai" && (
                    <div>
                        <label className="text-xs text-muted-foreground">Cheie OpenAI (sk-...)</label>
                        <input type="password" value={provider.ai_chat_openai_key} onChange={(e) => setProvider(p => ({ ...p, ai_chat_openai_key: e.target.value }))} placeholder="sk-..." className="input-soft font-mono text-xs" data-testid="openai-key" />
                        <p className="text-xs text-muted-foreground mt-1">Obții cheia din <a href="https://platform.openai.com/api-keys" target="_blank" rel="noopener noreferrer" className="text-primary underline">platform.openai.com/api-keys</a>. La "Model" mai jos pune: gpt-4o, gpt-4o-mini sau gpt-5.2.</p>
                    </div>
                )}
                {provider.ai_chat_provider === "anthropic" && (
                    <div>
                        <label className="text-xs text-muted-foreground">Cheie Anthropic (sk-ant-...)</label>
                        <input type="password" value={provider.ai_chat_anthropic_key} onChange={(e) => setProvider(p => ({ ...p, ai_chat_anthropic_key: e.target.value }))} placeholder="sk-ant-..." className="input-soft font-mono text-xs" data-testid="anthropic-key" />
                        <p className="text-xs text-muted-foreground mt-1">Obții cheia din <a href="https://console.anthropic.com/settings/keys" target="_blank" rel="noopener noreferrer" className="text-primary underline">console.anthropic.com</a>. Pune modelul dorit la "Model" mai jos (ex: claude-sonnet-4-5-20250929).</p>
                    </div>
                )}
                {provider.ai_chat_provider === "openrouter" && (
                    <div className="space-y-2">
                        <div>
                            <label className="text-xs text-muted-foreground">Cheie OpenRouter (sk-or-v1-...)</label>
                            <input type="password" value={provider.ai_chat_openrouter_key} onChange={(e) => setProvider(p => ({ ...p, ai_chat_openrouter_key: e.target.value }))} placeholder="sk-or-v1-..." className="input-soft font-mono text-xs" data-testid="openrouter-key" />
                            <p className="text-xs text-muted-foreground mt-1">Obții cheia din <a href="https://openrouter.ai/keys" target="_blank" rel="noopener noreferrer" className="text-primary underline">openrouter.ai/keys</a>. OpenRouter oferă acces la sute de modele, inclusiv modele gratuite.</p>
                        </div>
                        <div>
                            <label className="text-xs text-muted-foreground">Model OpenRouter</label>
                            <input value={cfg.ai_chat_model || "tencent/hy3-preview:free"} onChange={(e) => setCfg(c => ({ ...c, ai_chat_model: e.target.value }))} className="input-soft font-mono text-sm" placeholder="ex: tencent/hy3-preview:free" data-testid="openrouter-model" />
                            <p className="text-xs text-muted-foreground mt-1">Introdu exact ID-ul modelului din <a href="https://openrouter.ai/models" target="_blank" rel="noopener noreferrer" className="text-primary underline">openrouter.ai/models</a> (ex: <code className="bg-muted px-1 rounded">google/gemma-3-27b-it:free</code>, <code className="bg-muted px-1 rounded">meta-llama/llama-4-scout:free</code>).</p>
                        </div>
                    </div>
                )}
            </div>

            <div className="card-soft p-5 mb-4 space-y-3">
                <h3 className="font-heading text-lg">Personalizare aparență</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                        <label className="text-xs text-muted-foreground">Nume bot (afișat și în prompt)</label>
                        <input value={cfg.ai_chat_name || ""} onChange={(e) => setCfg(c => ({ ...c, ai_chat_name: e.target.value }))} className="input-soft" data-testid="chat-name" />
                    </div>
                    <div>
                        <label className="text-xs text-muted-foreground">Titlu afișat (header chat)</label>
                        <input value={cfg.ai_chat_title || ""} onChange={(e) => setCfg(c => ({ ...c, ai_chat_title: e.target.value }))} className="input-soft" />
                    </div>
                    <div>
                        <label className="text-xs text-muted-foreground">Subtitlu (ex: Consultant AI)</label>
                        <input value={cfg.ai_chat_subtitle || ""} onChange={(e) => setCfg(c => ({ ...c, ai_chat_subtitle: e.target.value }))} className="input-soft" />
                    </div>
                    {provider.ai_chat_provider !== "openrouter" && (
                    <div>
                        <label className="text-xs text-muted-foreground">Model</label>
                        {provider.ai_chat_provider === "openai" ? (
                            <input value={cfg.ai_chat_model || "gpt-4o-mini"} onChange={(e) => setCfg(c => ({ ...c, ai_chat_model: e.target.value }))} className="input-soft font-mono text-sm" placeholder="gpt-4o-mini" />
                        ) : (
                            <select value={cfg.ai_chat_model || "claude-sonnet-4-5-20250929"} onChange={(e) => setCfg(c => ({ ...c, ai_chat_model: e.target.value }))} className="input-soft">
                                <option value="claude-sonnet-4-5-20250929">Claude Sonnet 4.5 (implicit)</option>
                                <option value="claude-haiku-4-5-20250929">Claude Haiku 4.5 (rapid)</option>
                                <option value="claude-opus-4-5-20250929">Claude Opus 4.5 (premium)</option>
                            </select>
                        )}
                    </div>
                    )}
                </div>
                <div>
                    <label className="text-xs text-muted-foreground">Mesaj de salut (primul mesaj afișat utilizatorilor)</label>
                    <textarea rows="2" value={cfg.ai_chat_greeting || ""} onChange={(e) => setCfg(c => ({ ...c, ai_chat_greeting: e.target.value }))} className="input-soft" data-testid="chat-greeting" />
                </div>
            </div>

            <div className="card-soft p-5 mb-4 space-y-3">
                <h3 className="font-heading text-lg flex items-center gap-2"><MessageSquare className="h-5 w-5" /> Comportament & cunoștințe</h3>
                <p className="text-xs text-muted-foreground">System prompt-ul definește tonul și regulile. Lista de produse și categorii este atașată automat la final, nu trebuie să o copiezi.</p>
                <textarea rows="10" value={cfg.ai_chat_system_prompt || ""} onChange={(e) => setCfg(c => ({ ...c, ai_chat_system_prompt: e.target.value }))} placeholder={DEFAULT_PROMPT} className="input-soft font-mono text-xs" data-testid="chat-prompt" />
                <button onClick={() => setCfg(c => ({ ...c, ai_chat_system_prompt: DEFAULT_PROMPT }))} className="btn-outline text-sm">Folosește prompt-ul default</button>
            </div>

            <button onClick={save} disabled={saving} className="btn-primary" data-testid="chat-save">{saving ? "Se salvează..." : "Salvează setările"}</button>

            <div className="card-soft p-5 mt-8">
                <h3 className="font-heading text-lg mb-2">Testează bot-ul</h3>
                <p className="text-xs text-muted-foreground mb-3">Trimite un mesaj de test (va aplica setările curente și va interoga botul exact ca vizitatorii).</p>
                <div className="flex gap-2">
                    <input value={testMsg} onChange={(e) => setTestMsg(e.target.value)} placeholder="Ex: Caut o cameră pentru exterior, buget 500 RON" className="input-soft flex-1" data-testid="chat-test-input" />
                    <button onClick={testBot} disabled={testing} className="btn-primary" data-testid="chat-test-send">{testing ? "..." : "Trimite"}</button>
                </div>
                {testReply && <div className="mt-3 bg-muted rounded-xl p-3 text-sm whitespace-pre-line" data-testid="chat-test-reply">{testReply}</div>}
            </div>
        </div>
    );
}
